// ============================================================================
// PROPERTY TERMS HOOKS
// ============================================================================
// React hooks for Property Terms API operations

import { useState, useEffect, useCallback } from 'react';
import { propertyTermsService } from 'api/services/propertyTermsService';
import type {
  PropertyTerms,
  PropertyTermsSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all property terms (paginated)
 */
export const usePropertyTerms = (params?: PropertyTermsSearchRequest) => {
  const [data, setData] = useState<PropertyTerms[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchPropertyTerms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await propertyTermsService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchPropertyTerms();
  }, [fetchPropertyTerms]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchPropertyTerms,
  };
};

/**
 * Hook to delete property terms
 */
export const useDeletePropertyTerms = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deletePropertyTerms = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await propertyTermsService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deletePropertyTerms,
    loading,
    error,
    success,
  };
};
